(() => {
  if (window.luxairRetailCartLoaded) return;
  window.luxairRetailCartLoaded = true;
  const storageKey = 'luxair-retail-saved-v1';
  const root = () => document.getElementById('LuxairCart');
  let busy = false;
  let recommendationController;
  async function loadRecommendations(cart) {
    recommendationController?.abort();
    recommendationController = new AbortController();
    const signal = recommendationController.signal;
    const panel = cart.querySelector('[data-lc-recommendations]');
    if (!panel) return;
    panel.dataset.loading = 'true';
    const grid = panel.querySelector('[data-lc-recommendation-grid]');
    const seen = new Set([...grid.querySelectorAll('[data-lc-recommendation]')].map(el => el.dataset.lcRecommendation));
    panel.dataset.inCart.split(',').filter(Boolean).forEach(id => seen.add(id));
    const sources = [...panel.querySelectorAll('[data-lc-recommendation-source]')];
    // Fetch in basket order, stopping as soon as six useful products are available.
    for (const source of sources) {
      if (signal.aborted || !cart.isConnected || grid.children.length >= 6) break;
      try {
        const url = new URL(panel.dataset.endpoint, location.origin);
        if (url.origin !== location.origin) break;
        url.search = new URLSearchParams({section_id: 'luxair-cart-related', product_id: source.dataset.lcRecommendationSource, limit: '10', intent: 'related'}).toString();
        const response = await fetch(url, {credentials: 'same-origin', signal});
        if (!response.ok) continue;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted || !cart.isConnected) return;
        for (const card of html.querySelectorAll('[data-lc-related-results] [data-lc-recommendation]')) {
          const id = card.dataset.lcRecommendation;
          if (seen.has(id) || grid.children.length >= 6) continue;
          seen.add(id);
          // A recommendation arriving during a cart write must not become clickable.
          card.querySelectorAll('button,input').forEach(el => { el.disabled = busy; });
          grid.append(card);
        }
        panel.hidden = grid.children.length === 0;
      } catch (_) {
        // Recommendations are optional; their failure must never block checkout.
      }
    }
    if (cart.isConnected && !signal.aborted) panel.dataset.loading = 'false';
  }
  const safeUrl = (value, image = false) => {
    try {
      const url = new URL(value, location.origin);
      if (image && url.protocol === 'https:' && (url.hostname === location.hostname || url.hostname === 'cdn.shopify.com')) return url.href;
      if (!image && url.origin === location.origin) return url.href;
    } catch (_) { /* Invalid saved URLs are ignored. */ }
    return '';
  };
  function savedItems() {
    try {
      const items = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(items) ? items.filter(item => /^\d+$/.test(String(item.id)) && typeof item.title === 'string' && safeUrl(item.url)).slice(0, 30) : [];
    } catch (_) { return []; }
  }
  function saveItems(items) {
    localStorage.setItem(storageKey, JSON.stringify(items.slice(0, 30)));
  }
  function message(value) {
    const target = root()?.querySelector('[data-lc-status]');
    if (target) target.textContent = value;
  }
  function announce(value) {
    const target = root()?.querySelector('[data-lc-announcement]');
    if (target) target.textContent = value;
  }
  function lock(value) {
    busy = value;
    const cart = root();
    if (!cart) return;
    cart.setAttribute('aria-busy', String(value));
    cart.querySelectorAll('button,input,textarea').forEach(el => { el.disabled = value; });
    // Express checkout must not bypass a pending cart update.
    cart.querySelectorAll('.lc-express').forEach(el => { el.inert = value; });
  }
  function renderSaved() {
    const container = root()?.querySelector('[data-lc-saved]');
    if (!container) return;
    const grid = container.querySelector('[data-lc-saved-grid]');
    const items = savedItems();
    container.hidden = !items.length;
    grid.replaceChildren();
    items.forEach(item => {
      const card = document.createElement('article');
      card.className = 'lc-small-card';
      const imgUrl = safeUrl(item.image, true);
      if (imgUrl) {
        const img = document.createElement('img');
        img.src = imgUrl; img.alt = item.title; img.loading = 'lazy'; card.append(img);
      }
      const link = document.createElement('a');
      link.href = safeUrl(item.url); link.textContent = item.title; card.append(link);
      const add = document.createElement('button');
      add.type = 'button'; add.className = 'lc-button'; add.dataset.lcRestore = item.id;
      add.textContent = 'Move to basket'; card.append(add);
      const remove = document.createElement('button');
      remove.type = 'button'; remove.className = 'lc-text-button'; remove.dataset.lcForget = item.id;
      remove.textContent = 'Remove saved item'; card.append(remove); grid.append(card);
    });
  }
  function init() {
    const cart = root();
    if (!cart) return;
    cart.classList.add('lc-enhanced');
    renderSaved();
    loadRecommendations(cart);
  }
  async function renderSection(data, context) {
    const sectionId = root().dataset.sectionId;
    let html = data.sections?.[sectionId];
    if (!html) {
      const url = new URL(root().dataset.cartUrl, location.origin);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store'});
      if (!response.ok) throw new Error('The basket changed, but could not be refreshed. Reload this page before continuing.');
      html = await response.text();
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const updated = parsed.getElementById('LuxairCart');
    if (!updated) throw new Error('The basket changed, but could not be refreshed. Reload this page before continuing.');
    root().replaceWith(updated);
    const note = root().querySelector('[data-lc-note]');
    if (note && context.note !== undefined) { note.value = context.note; if (context.noteOpen) note.closest('details').open = true; }
    const terms = root().querySelector('#LCTerms');
    if (terms) terms.checked = context.terms;
    init();
    if (window.Shopify?.StorefrontExpressButtons) window.Shopify.StorefrontExpressButtons.initialize();
  }
  async function mutate(path, payload, options = {}) {
    if (busy || !root()) return false;
    const cart = root();
    const note = cart.querySelector('[data-lc-note]');
    const active = document.activeElement;
    const focusId = active?.id;
    const focusLine = active?.closest('[data-line-key]')?.dataset.lineKey;
    const context = {note: note?.value, noteOpen: note?.closest('details').open, terms: Boolean(cart.querySelector('#LCTerms')?.checked)};
    message(''); lock(true);
    let confirmed = false;
    try {
      const response = await fetch(`${cart.dataset.rootUrl.replace(/\/$/, '')}/cart/${path}.js`, {
        method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({...payload, sections: [cart.dataset.sectionId], sections_url: cart.dataset.cartUrl})
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'This item could not be updated. Please check availability and try again.');
        error.rejected = true; throw error;
      }
      confirmed = true;
      options.onSuccess?.();
      await renderSection(data, context);
      announce(options.announcement || 'Basket updated. Prices and savings recalculated.');
      return true;
    } catch (error) {
      if (!confirmed && error.rejected) options.onRejected?.();
      if (confirmed || !error.rejected) {
        // Do not retry a write: a lost response may already have changed the basket.
        message('We could not confirm the latest basket display. Reload this page to check your basket before continuing.');
        const refresh = document.createElement('a');
        refresh.href = cart.dataset.cartUrl; refresh.textContent = ' Reload basket';
        root()?.querySelector('[data-lc-status]')?.append(refresh);
        options.uncertain = true;
      } else {
        // An inventory rejection can still change available quantities. Read back the
        // server-rendered basket rather than leaving a stale subtotal on screen.
        try { await renderSection({}, context); message(error.message); }
        catch (_) {
          message('Please reload your basket to check availability before continuing.');
          const refresh = document.createElement('a');
          refresh.href = cart.dataset.cartUrl; refresh.textContent = ' Reload basket';
          root()?.querySelector('[data-lc-status]')?.append(refresh);
          options.uncertain = true;
        }
      }
      return false;
    } finally {
      lock(false);
      if (options.uncertain) {
        lock(true);
        // Reload and navigation remain available; prevent duplicate writes and stale checkout.
      } else {
        const target = focusId ? document.getElementById(focusId) : null;
        const line = focusLine ? [...root().querySelectorAll('[data-line-key]')].find(el => el.dataset.lineKey === focusLine) : null;
        const fallback = target || line?.querySelector('button') || root().querySelector('[data-lc-continue]');
        fallback?.focus({preventScroll: true});
      }
    }
  }
  document.addEventListener('click', async event => {
    const cart = root();
    if (!cart || !cart.contains(event.target)) return;
    const control = event.target.closest('[data-lc-quantity],[data-lc-remove],[data-lc-save],[data-lc-restore],[data-lc-forget]');
    if (!control) return;
    event.preventDefault();
    if (busy) return;
    if (control.hasAttribute('data-lc-quantity') || control.hasAttribute('data-lc-remove')) {
      await mutate('change', {id: control.dataset.key, quantity: Number(control.dataset.lcQuantity || 0)});
    } else if (control.hasAttribute('data-lc-save')) {
      const original = savedItems();
      const item = {id: control.dataset.variant, title: control.dataset.title, url: control.dataset.url, image: control.dataset.image, quantity: Number(control.dataset.quantity)};
      try { saveItems([item, ...original.filter(saved => saved.id !== item.id)]); }
      catch (_) { message('Your browser could not save this item. It has been kept in your basket.'); return; }
      await mutate('change', {id: control.dataset.key, quantity: 0}, {onRejected: () => saveItems(original), announcement: 'Item saved for later on this device.'});
    } else if (control.hasAttribute('data-lc-restore')) {
      const item = savedItems().find(saved => saved.id === control.dataset.lcRestore);
      if (!item) return;
      await mutate('add', {items: [{id: item.id, quantity: Number.isSafeInteger(item.quantity) && item.quantity > 0 ? item.quantity : 1}]}, {
        onSuccess: () => saveItems(savedItems().filter(saved => saved.id !== item.id)), announcement: 'Saved item moved to your basket.'
      });
    } else {
      try { saveItems(savedItems().filter(item => item.id !== control.dataset.lcForget)); renderSaved(); announce('Saved item removed.'); }
      catch (_) { message('Your browser could not update your saved items.'); }
    }
  });
  document.addEventListener('change', async event => {
    const input = event.target;
    if (!root()?.contains(input) || !input.matches('[data-lc-input]')) return;
    const quantity = Number(input.value);
    if (!Number.isSafeInteger(quantity) || quantity < 0) { message('Please enter a whole quantity of zero or more.'); input.value = input.defaultValue; return; }
    await mutate('change', {id: input.dataset.key, quantity});
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!root()?.contains(form)) return;
    if (busy) { event.preventDefault(); return; }
    if (form.matches('[data-lc-add-form]')) {
      event.preventDefault();
      await mutate('add', {items: [{id: new FormData(form).get('id'), quantity: 1}]});
    } else if (form.id === 'LuxairCartForm' && !event.submitter?.hasAttribute('data-lc-checkout')) {
      event.preventDefault();
      const updates = {};
      form.querySelectorAll('[data-lc-input]').forEach(input => { updates[input.dataset.key] = Number(input.value); });
      await mutate('update', {updates});
    }
  });
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('storage', event => { if (event.key === storageKey) renderSaved(); });
  init();
})();
